import { Bounds, OrbitControls, PerspectiveCamera } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useIntl } from "react-intl";
import { Box3, BufferGeometry, Float32BufferAttribute, LineBasicMaterial, LineSegments, Sprite, SpriteMaterial, SRGBColorSpace, Texture, Vector3 } from "three";
import AxesHelper from "./helper/AxesHelper.jsx";
import ErrorComponent from "./helper/ErrorComponent.jsx";
import LightElements from "./helper/LightElements.jsx";
import ModelFBX from "./models/ModelFBX.jsx";
import ModelGLTF from "./models/ModelGLTF.jsx";
import ModelOBJ from "./models/ModelOBJ.jsx";
import ModelOBJZip from "./models/ModelOBJZip.jsx";

/**
 * Centralizes the 3d-model in the middle of the view by adjusting the controls.
 * @returns 
 */
const centralizeControls = (controlRef, bounds, gl) => {
  if (!gl) return;
  if (controlRef.current) {
    controlRef.current.reset();
  }
  if (bounds) {
    // refresh bounds
    bounds.refresh().clip().fit();
  }
};

/**
 * Centralizes the given model in the center of the plane and aligns the bottom on the plane.
 * Also draws the bounding-box-lines of the model.
 */
const centralizeModel = (model, lineRef, scene, data, intl) => {
  if (model) {
    const box3 = new Box3().setFromObject(model);
    // position the bottom of the model on the x-z-plane
    model.position.y -= box3.min.y;
    // center the model in the center of the x-z-plane
    model.position.z -= (box3.max.z + box3.min.z) / 2;
    model.position.x -= (box3.max.x + box3.min.x) / 2;
  }
  drawBoundingBoxLines(model, lineRef, scene, data, intl);
};

/**
 * Draws the bounding-box-lines of the model.
 * Draws lines parallel to the x-, y- and z-axis.
 * Displays the respective size as text-labels next to the lines.
 * @param {*} model 
 * @param {*} lineRef 
 * @param {*} scene 
 */
const drawBoundingBoxLines = (model, lineRef, scene, data, intl) => {
  // if lineRef is not set, do not draw bounding-boxes (for example in case of screenshot)
  if (!lineRef) return;
  if (model && scene) {
    if (lineRef.current) {
      // remove existing bounding-boxes
      scene.remove(lineRef.current.lines);
      lineRef.current.geometry.dispose();
      lineRef.current.material.dispose();
      // remove existing text-labels
      lineRef.current.textSprites.forEach(sprite => {
        scene.remove(sprite);
        sprite.material.map.dispose();
        sprite.material.dispose();
      });
    }

    // Compute the bounding box
    const boundingBox = new Box3().setFromObject(model);
    // get the maximum size of the bounding box
    const size = new Vector3();
    boundingBox.getSize(size);
    const minimumSize = Math.min(size.x, size.y, size.z);
    // create an offset factor to scale the offset of the bounding-box-lines and -labels
    // the offset-factor has a maximum-value
    const offsetFactor = Math.min(minimumSize / 4.0, 0.5);

    // Get the min and max points of the bounding box
    const min = boundingBox.min;
    const max = boundingBox.max;

    // Create lines for the X, Y, Z dimensions
    const geometry = new BufferGeometry();
    const vertices = [
      // parallel to X-axis line
      min.x, min.y, max.z + 0.5 * offsetFactor, max.x, min.y, max.z + 0.5 * offsetFactor, // Bottom edge
      min.x, min.y, max.z + 0.7 * offsetFactor, min.x, min.y, max.z + 0.3 * offsetFactor, // Left end for bottom edge
      max.x, min.y, max.z + 0.7 * offsetFactor, max.x, min.y, max.z + 0.3 * offsetFactor, // Right end for bottom edge

      // parallel to Y-axis line
      max.x + 0.5 * offsetFactor, min.y, max.z, max.x + 0.5 * offsetFactor, max.y, max.z, // Right-front edge
      max.x + 0.7 * offsetFactor, max.y, max.z, max.x + 0.3 * offsetFactor, max.y, max.z, // Top end for right front edge
      max.x + 0.7 * offsetFactor, min.y, max.z, max.x + 0.3 * offsetFactor, min.y, max.z, // Bottom end for right front edge

      // parallel to Z-axis lines
      max.x, max.y + 0.5 * offsetFactor, min.z, max.x, max.y + 0.5 * offsetFactor, max.z, // Top-right edge,
      max.x + 0.2 * offsetFactor, max.y + 0.5 * offsetFactor, max.z, max.x - 0.2 * offsetFactor, max.y + 0.5 * offsetFactor, max.z, // Front end of top right edge
      max.x + 0.2 * offsetFactor, max.y + 0.5 * offsetFactor, min.z, max.x - 0.2 * offsetFactor, max.y + 0.5 * offsetFactor, min.z, // Back end of top right edge
    ];

    geometry.setAttribute(
      'position',
      new Float32BufferAttribute(vertices, 3)
    );

    const material = new LineBasicMaterial({ color: 0x000000 });

    const lines = new LineSegments(geometry, material);

    // Helper function to create text sprites
    const createTextSprite = (text, position, color, size) => {
      const fontSize = 4 * size * offsetFactor;
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      // size the canvas according to the text
      // set font-size
      context.font = `${fontSize}px Arial`;
      const metrics = context.measureText(text);
      canvas.width = metrics.width; // Add some padding
      canvas.height = fontSize + 20;


      // Draw the text on the canvas
      // set font-size again, because it has been resetted
      context.font = `${fontSize}px Arial`;
      context.fillStyle = color;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(text, canvas.width / 2, canvas.height / 2);

      const texture = new Texture(canvas);
      texture.needsUpdate = true;
      const spriteMaterial = new SpriteMaterial({ map: texture, transparent: true })
      const sprite = new Sprite(spriteMaterial);
      sprite.position.set(...position);
      sprite.scale.set(offsetFactor * canvas.width / 100, offsetFactor * canvas.height / 100, 1);
      return sprite;
    };

    // Create text sprites for dimensions
    const labels = [
      {
        text: data.length ? `${data.length}mm` : '',
        position: [(min.x + max.x) / 2, min.y + 0.3 * offsetFactor, max.z + 1.5 * offsetFactor],
        color: 'black',
        size: 40
      },
      {
        text: data.height ? `${data.height}mm` : '',
        position: [max.x + 1.6 * offsetFactor, (min.y + max.y) / 2, max.z],
        color: 'black',
        size: 40
      },
      {
        text: data.depth ? `${data.depth}mm` : '',
        position: [max.x, max.y + 1 * offsetFactor, (min.z + max.z) / 2],
        color: 'black',
        size: 40
      },
      {
        text: intl.formatMessage({ id: "EditModel.front" }),
        position: [- offsetFactor, 0, max.z + 3 * offsetFactor],
        color: 'blue',
        size: 50
      },
      {
        text: intl.formatMessage({ id: "EditModel.side" }),
        position: [max.x + 3 * offsetFactor, 0, 0 + 1 * offsetFactor],
        color: 'red',
        size: 50
      },
      {
        text: intl.formatMessage({ id: "EditModel.top" }),
        position: [- offsetFactor, max.y + 1.5 * offsetFactor, 0],
        color: 'green',
        size: 50
      }
    ];

    // Add the text labels to the scene
    const textSprites = labels.filter(label => label.text).map((label) =>
      createTextSprite(label.text, label.position, label.color, label.size)
    );

    lineRef.current = { lines, material, geometry, textSprites };
    // Add the lines to the scene or model group
    scene.add(lines);
    textSprites.forEach((sprite) => scene.add(sprite));
  } else if (!model && scene && lineRef.current) {
    // remove existing bounding-boxes
    scene.remove(lineRef.current.lines);
    // remove existing text labels
    lineRef.current.textSprites.forEach(sprite => {
      scene.remove(sprite);
      sprite.material.map.dispose();
      sprite.material.dispose();
    });
    lineRef.current.geometry.dispose();
    lineRef.current.material.dispose();
  }
}

const zoom = (value, pos) => {
  pos = pos.normalize();
  return [pos.x * value, pos.y * value, pos.z * value]
};

/**
 * Initializes the rotation of the specified model with the specified angles.
 * @param {*} model the model to rotate
 * @param {*} rotation the rotation in form of a quaternion
 */
const initializeRotation = (model, rotation) => {
  if (model && rotation) {
    model.setRotationFromQuaternion(rotation);
  }
}

/**
 * Returns the file-type of the 3d-model specified by fileName and contentType.
 * @param {*} fileName 
 * @param {*} contentType 
 * @returns 'fbx' | 'glb' | 'gltf' | 'obj' | 'objZip' | null
 */
const getModelFileType = (fileName, contentType) => {
  switch (contentType) {
    case 'model/fbx':
      return 'fbx';
    case 'model/gltf-binary':
      return 'glb';
    case 'model/gltf+json':
      return 'gltf';
    case 'model/obj':
      return 'obj';
    case 'application/zip':
      return 'objZip';
    default:
  }
  // if none already matched, return according to fileName
  if (/\.fbx$/i.test(fileName)) {
    return 'fbx';
  } else if (/\.gltf$/i.test(fileName)) {
    return 'gltf';
  } else if (/\.glb$/i.test(fileName)) {
    return 'glb';
  } else if (/\.obj$/i.test(fileName)) {
    return 'obj';
  } else if (/\.zip/i.test(fileName)) {
    return 'objZip';
  } else {
    return null;
  }
}

const ModelPreview = forwardRef(({ url, data, contentType, fileName, adjustScale, zoomValue, rotation, onLoad }, ref) => {

  const { controlRef, sceneRef, lineRef, meshRef } = ref;

  const cameraRef = useRef();

  const intl = useIntl();

  const [axisThickness, setAxisThickness] = useState(0.02);

  useEffect(() => {
    if (cameraRef.current) {
      cameraRef.current.position.set(...zoom(zoomValue, new Vector3(1.0, 1.5, 3.0)));
    }
  }, []);

  useEffect(() => {
    if (cameraRef.current) {
      cameraRef.current.position.set(...zoom(zoomValue, cameraRef.current.position));
    }
  }, [zoomValue]);

  /**
   * Update the bounding-box-lines, when the data changes.  
   */
  useEffect(() => {
    drawBoundingBoxLines(meshRef.current, lineRef, sceneRef.current, data, intl);
  }, [data.length, data.height, data.depth]);

  const modelElement = useMemo(() => {
    const fileType = getModelFileType(fileName, contentType);
    if (fileType === 'fbx') {
      return <ModelFBX url={url} data={data} adjustScale={adjustScale} ref={ref} rotation={rotation} setAxisThickness={setAxisThickness} onLoad={onLoad} intl={intl} />;
    } else if (fileType === 'gltf' || fileType === 'glb') {
      return <ModelGLTF url={url} data={data} adjustScale={adjustScale} ref={ref} rotation={rotation} setAxisThickness={setAxisThickness} onLoad={onLoad} intl={intl} />;
    } else if (fileType === 'obj') {
      return <ModelOBJ url={url} data={data} adjustScale={adjustScale} ref={ref} rotation={rotation} onLoad={onLoad} intl={intl} />;
    } else if (fileType === 'objZip') {
      // unzip file
      return <ModelOBJZip url={url} data={data} adjustScale={adjustScale} ref={ref} rotation={rotation} onLoad={onLoad} intl={intl} />
    } else {
      // calling method with model=null results in existing bounding-boxes to be removed
      drawBoundingBoxLines(null, lineRef, sceneRef.current, data, intl)
      return null;
    }
  }, [url, data, fileName, rotation])

  return (
    <ErrorBoundary FallbackComponent={ErrorComponent}>
      <Canvas
        color={0xffffff}
        linear
        gl={{ outputColorSpace: SRGBColorSpace, depth: true }}
        onCreated={({ gl }) => {
          gl.shadowMap.enabled = true;
        }}
      >
        <LightElements />

        {url && (
          <Bounds fit clip damping={6} maxDuration={0} margin={1.2} >
            {modelElement}
          </Bounds>
        )}

        <PerspectiveCamera makeDefault ref={cameraRef} onUpdate={(self) => self.position.set(...zoom(zoomValue, new Vector3(1.0, 1.5, 3.0)))} />
        <OrbitControls makeDefault enablePan={false} ref={controlRef} />
        <gridHelper args={[50, 50]} />
        <AxesHelper axisThickness={axisThickness} />
      </Canvas>
    </ErrorBoundary>
  );
});

export { centralizeControls, centralizeModel, getModelFileType, initializeRotation, zoom };
export default ModelPreview;